/**
 * Trailer standing pool → Artic (or chosen class) allocation.
 * Keep in sync with zeecore FleetCostConfigService trailer helpers
 * (asserted by app fleetCostTrailerStanding.test.ts and
 * zeecore trailerStandingContract.test.ts).
 */

import type {
  FleetCostAssumptionsConfig,
  TrailerStandingAssumptions,
  TrailerStandingQuantitySource,
  TrailerTypeRow,
} from '../types/fleetCostAssumptions';
import {
  acquisitionMethodOf,
  vehicleCapitalAnnualisedGbp,
  withAcquisitionMixFromAssets,
} from './fleetCapitalAnnualised';

/** Same calendar-day basis as Standing £/yr → £/day. */
export const TRAILER_STANDING_DAYS_PER_YEAR = 365;

/** Default £/yr per trailer (insurance + inspections + fixed maintenance). */
export const DEFAULT_TRAILER_STANDING_GBP_PER_YEAR = 2800;

export const DEFAULT_TRAILER_STANDING: TrailerStandingAssumptions = {
  standing_gbp_per_trailer_year: DEFAULT_TRAILER_STANDING_GBP_PER_YEAR,
  trailer_quantity: 0,
  allocate_to_vehicle_quantity: 0,
  quantity_source: 'assets',
  allocation: 'artic_fleet_average',
  allocate_to_tonnage_class: 'Artic',
};

/** Body-style peer that also receives Artic trailer allocation. */
export const TRAILER_ALLOCATION_BODY_STYLE_PEER = 'Tractor Unit';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function normalizeTrailerStanding(
  value: TrailerStandingAssumptions | undefined | null,
): TrailerStandingAssumptions {
  const src = (value ?? {}) as Partial<TrailerStandingAssumptions>;
  const quantity_source: TrailerStandingQuantitySource =
    src.quantity_source === 'manual' ? 'manual' : 'assets';
  return {
    standing_gbp_per_trailer_year:
      typeof src.standing_gbp_per_trailer_year === 'number'
        && Number.isFinite(src.standing_gbp_per_trailer_year)
        ? Math.max(0, src.standing_gbp_per_trailer_year)
        : DEFAULT_TRAILER_STANDING.standing_gbp_per_trailer_year,
    trailer_quantity:
      typeof src.trailer_quantity === 'number' && Number.isFinite(src.trailer_quantity)
        ? Math.max(0, src.trailer_quantity)
        : DEFAULT_TRAILER_STANDING.trailer_quantity,
    allocate_to_vehicle_quantity:
      typeof src.allocate_to_vehicle_quantity === 'number'
        && Number.isFinite(src.allocate_to_vehicle_quantity)
        ? Math.max(0, src.allocate_to_vehicle_quantity)
        : DEFAULT_TRAILER_STANDING.allocate_to_vehicle_quantity,
    quantity_source,
    allocation: src.allocation === 'none' ? 'none' : 'artic_fleet_average',
    allocate_to_tonnage_class:
      String(src.allocate_to_tonnage_class || '').trim()
      || DEFAULT_TRAILER_STANDING.allocate_to_tonnage_class,
    ...(Array.isArray(src.trailer_types) && src.trailer_types.length > 0
      ? { trailer_types: src.trailer_types.map(normalizeTrailerType) }
      : {}),
  };
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeTrailerType(row: TrailerTypeRow, index: number): TrailerTypeRow {
  return {
    ...row,
    id: String(row?.id ?? '').trim() || `trailer-type-${index + 1}`,
    name: String(row?.name ?? '').trim() || `Trailer type ${index + 1}`,
    acquisitionMethod: acquisitionMethodOf(row ?? {}),
    quantity: Math.max(0, finiteOr(row?.quantity, 0)),
    standing_gbp_per_trailer_year: Math.max(
      0,
      finiteOr(row?.standing_gbp_per_trailer_year, DEFAULT_TRAILER_STANDING_GBP_PER_YEAR),
    ),
  };
}

/** £/yr per trailer of one type = capital (purchase / lease / rental) + standing. */
export function trailerTypeAnnualPerTrailerGbp(row: TrailerTypeRow): number {
  // Count-weighted by acquisition, as for vehicles (zeecore trailerTypeAnnualPerTrailerGbp).
  return vehicleCapitalAnnualisedGbp(row) + Math.max(0, finiteOr(row.standing_gbp_per_trailer_year, 0));
}

/**
 * Starting rows when an org first splits its pool into types (all leased).
 * Every current trailer starts on the first type at the current standing
 * rate, so splitting and saving without edits leaves the pool unchanged;
 * the user then moves trailers to other types and enters lease costs.
 */
export function defaultTrailerTypes(standingPerYear: number, trailerQty = 0): TrailerTypeRow[] {
  return [
    {
      id: 'single-deck',
      name: 'Single deck',
      quantity: Math.max(0, trailerQty),
      standing_gbp_per_trailer_year: standingPerYear,
      acquisitionMethod: 'lease',
      leaseMonthly: 0,
      leaseTermMonths: 60,
    },
    {
      id: 'double-deck',
      name: 'Double deck',
      quantity: 0,
      standing_gbp_per_trailer_year: standingPerYear,
      acquisitionMethod: 'lease',
      leaseMonthly: 0,
      leaseTermMonths: 60,
    },
  ];
}

/**
 * Resolve display/persist quantities.
 * - assets: overlay Asset counts (including intentional display of live roster)
 * - manual: keep stored numbers, including 0
 */
export function resolveTrailerStandingQtys(
  trailer: TrailerStandingAssumptions,
  opts: {
    articQty: number;
    trailerQty: number;
    deckCounts?: TrailerDeckCounts;
    acquisitionByDeck?: TrailerAcquisitionByDeck;
  },
): TrailerStandingAssumptions {
  const n = normalizeTrailerStanding(trailer);
  if (n.quantity_source !== 'assets') return n;
  return {
    ...n,
    trailer_quantity: Math.max(0, opts.trailerQty),
    allocate_to_vehicle_quantity: Math.max(0, opts.articQty),
    ...(n.trailer_types?.length && (opts.deckCounts || opts.acquisitionByDeck)
      ? {
          trailer_types: withTrailerAcquisitionMix(
            withDeckQuantities(n.trailer_types, opts.deckCounts),
            opts.acquisitionByDeck,
          ),
        }
      : {}),
  };
}

/** Trailers on Assets by Deck. `unset` = a trailer with no Deck chosen yet. */
export interface TrailerDeckCounts {
  single: number;
  double: number;
  unset: number;
}

/**
 * Deck read from a source system's vehicle type, e.g. Aquarius "DD Trailer".
 * Trailers only; undefined when the type does not say. An Assets Deck edit
 * always wins over this.
 */
export function trailerDeckFromVehicleType(type: string | null | undefined): 'single' | 'double' | undefined {
  const t = String(type ?? '');
  if (countTrailerAssets([{ type: t }]) !== 1) return undefined;
  if (/\bDD\b|double[\s-]*deck/i.test(t)) return 'double';
  if (/\bSD\b|single[\s-]*deck/i.test(t)) return 'single';
  return undefined;
}

/** Count Assets trailers by Deck (same trailer rule as countTrailerAssets). */
export function countTrailerDecks(
  labels: Array<{ type: string; payloadTonnes?: string; trailer_deck?: 'single' | 'double' }> | null | undefined,
): TrailerDeckCounts {
  const out: TrailerDeckCounts = { single: 0, double: 0, unset: 0 };
  for (const a of labels ?? []) {
    if (countTrailerAssets([a]) !== 1) continue;
    out[a.trailer_deck ?? trailerDeckFromVehicleType(a.type) ?? 'unset'] += 1;
  }
  return out;
}

/** Trailers on Assets by Acquisition, per deck column (unset deck counts as single, as in withDeckQuantities). */
export type TrailerAcquisitionByDeck = Record<
  'single' | 'double',
  { purchase: number; lease: number; rental: number; unset: number }
>;

export function countTrailerAcquisitionByDeck(
  labels: Array<{
    type: string;
    payloadTonnes?: string;
    trailer_deck?: 'single' | 'double';
    acquisition?: 'purchase' | 'lease' | 'rental';
  }> | null | undefined,
): TrailerAcquisitionByDeck {
  const empty = () => ({ purchase: 0, lease: 0, rental: 0, unset: 0 });
  const out: TrailerAcquisitionByDeck = { single: empty(), double: empty() };
  for (const a of labels ?? []) {
    if (countTrailerAssets([a]) !== 1) continue;
    const deck = a.trailer_deck ?? trailerDeckFromVehicleType(a.type) ?? 'single';
    out[deck][a.acquisition ?? 'unset'] += 1;
  }
  return out;
}

/**
 * Single / double deck counts by acquisition from Assets, as vehicle classes
 * get theirs (withAcquisitionMixFromAssets). A deck with no trailers clears
 * its counts, so it falls back to its own method. Other types are unchanged.
 */
export function withTrailerAcquisitionMix(
  types: TrailerTypeRow[],
  byDeck: TrailerAcquisitionByDeck | null | undefined,
): TrailerTypeRow[] {
  if (!byDeck) return types;
  const has = (m: TrailerAcquisitionByDeck['single']) => m.purchase + m.lease + m.rental + m.unset > 0;
  return types.map((t) => {
    const deck = t.id === 'single-deck' ? 'single' : t.id === 'double-deck' ? 'double' : null;
    if (!deck) return t;
    return withAcquisitionMixFromAssets(t, has(byDeck[deck]) ? byDeck[deck] : undefined);
  });
}

/** True when Assets says which deck at least one trailer is: then the counts drive the types. */
export function deckCountsApply(counts: TrailerDeckCounts | null | undefined): counts is TrailerDeckCounts {
  return Boolean(counts && counts.single + counts.double > 0);
}

/**
 * Single / double deck quantities from Assets. Trailers with no Deck set are
 * costed as single deck (shown as not set, never as known single deck). Other
 * types keep their entered quantity. No-op until a trailer has a Deck.
 */
export function withDeckQuantities(
  types: TrailerTypeRow[],
  counts: TrailerDeckCounts | null | undefined,
): TrailerTypeRow[] {
  if (!deckCountsApply(counts)) return types;
  return types.map((t) =>
    t.id === 'single-deck'
      ? { ...t, quantity: counts.single + counts.unset }
      : t.id === 'double-deck'
        ? { ...t, quantity: counts.double }
        : t,
  );
}

export function trailerStandingPoolAnnualGbp(t: TrailerStandingAssumptions): number {
  const n = normalizeTrailerStanding(t);
  if (n.trailer_types?.length) {
    return n.trailer_types.reduce(
      (sum, row) => sum + trailerTypeAnnualPerTrailerGbp(row) * row.quantity,
      0,
    );
  }
  return n.standing_gbp_per_trailer_year * n.trailer_quantity;
}

/**
 * Annual trailer share per receiving-class vehicle (= pool ÷ vehicle qty).
 * £/day is derived from this so UI £/yr and Profit £/day stay consistent.
 */
export function trailerStandingAllocatedAnnualPerVehicleGbp(
  t: TrailerStandingAssumptions,
): number {
  const n = normalizeTrailerStanding(t);
  if (n.allocation === 'none') return 0;
  const pool = trailerStandingPoolAnnualGbp(n);
  if (pool <= 0 || n.allocate_to_vehicle_quantity <= 0) return 0;
  return round2(pool / n.allocate_to_vehicle_quantity);
}

/** £/day add-on = annual share ÷ 365 (2dp). */
export function trailerStandingAddOnPerDayGbp(t: TrailerStandingAssumptions): number {
  const annual = trailerStandingAllocatedAnnualPerVehicleGbp(t);
  if (annual <= 0) return 0;
  return round2(annual / TRAILER_STANDING_DAYS_PER_YEAR);
}

export function trailerStandingFromConfig(
  config: FleetCostAssumptionsConfig | null | undefined,
): TrailerStandingAssumptions {
  return normalizeTrailerStanding(config?.trailer_standing);
}

/**
 * Count trailer-like assets from roster labels.
 * Matches type/payload containing trailer (incl. drawbar / tipper trailer).
 */
export function countTrailerAssets(
  labels: Array<{ type: string; payloadTonnes?: string }> | null | undefined,
): number {
  if (!labels?.length) return 0;
  return labels.filter((a) => {
    const type = String(a.type ?? '').trim();
    const payload = String(a.payloadTonnes ?? '').trim();
    return /trailer|drawbar/i.test(type) || /trailer/i.test(payload);
  }).length;
}

export function withTrailerStandingAssetPrefill(
  config: FleetCostAssumptionsConfig,
  opts: { articQty: number; trailerQty: number },
): FleetCostAssumptionsConfig {
  return {
    ...config,
    trailer_standing: resolveTrailerStandingQtys(trailerStandingFromConfig(config), opts),
  };
}

/** Canonical numbers for app↔zeecore contract tests. */
export const TRAILER_STANDING_CONTRACT = {
  defaultGbpPerYear: DEFAULT_TRAILER_STANDING_GBP_PER_YEAR,
  daysPerYear: TRAILER_STANDING_DAYS_PER_YEAR,
  bodyStylePeer: TRAILER_ALLOCATION_BODY_STYLE_PEER,
  /** pool 2800×10, artic qty 5 → annual share 5600, day 15.34 */
  example: {
    perTrailer: 2800,
    trailerQty: 10,
    articQty: 5,
    pool: 28_000,
    annualPerVehicle: 5600,
    dayPerVehicle: 15.34,
  },
  /**
   * By type: single deck lease £400/mo + £2800 standing × 10,
   * double deck lease £600/mo + £3000 standing × 5, 20 Artic.
   */
  byTypeExample: {
    types: [
      { id: 'single-deck', name: 'Single deck', quantity: 10, standing_gbp_per_trailer_year: 2800, acquisitionMethod: 'lease', leaseMonthly: 400, leaseTermMonths: 60 },
      { id: 'double-deck', name: 'Double deck', quantity: 5, standing_gbp_per_trailer_year: 3000, acquisitionMethod: 'lease', leaseMonthly: 600, leaseTermMonths: 60 },
    ],
    articQty: 20,
    pool: 127_000,
    annualPerVehicle: 6350,
    dayPerVehicle: 17.4,
  },
} as const;
